import { useCallback, useEffect, useRef, useState } from "react";
import {
  pickOutputDeviceClass,
  computeDynamicThresholds,
} from "@/hooks/_voiceUtils";
import { getAdjustedBargeInThreshold } from "@/hooks/_voiceTelemetry";

/**
 * usePreWarmedMic — pre-warms the mic and exposes simple VAD plus mute /
 * barge-in helpers used by useJarvisVoice.
 *
 * Goals:
 *   - Reduce perceived delay between "user clicks mic" and "Nexo listening".
 *   - Pre-warm: getUserMedia silently on the first user gesture.
 *   - VAD: AnalyserNode + RMS loop. Emits onVoiceStart / onVoiceEnd.
 *   - Hardware mute: track.enabled flip while TTS is speaking, so the mic
 *     cannot pick up the assistant's own audio (echo loop fix on speakers).
 *   - Barge-in detection: a separate, higher RMS threshold that fires
 *     onBargeIn when the user starts talking *during* TTS. Sustained for
 *     `bargeInHoldMs` to avoid firing on brief background noise.
 *
 * API:
 *   { state, isSpeaking, prewarm, ensurePrewarmed, teardown,
 *     setMicEnabled, setBargeInMode, isOutputSpeaker }
 */

// VOICE-POLISH-V8 — Bug A fix: V2-V7 defaults eram 0.050/200ms que
// disparavam falso positivo em ambiente com ar-condicionado ou mic
// sensível (cough/breath/ruído contínuo cortava TTS no meio). Sobe
// pra 0.075/400ms — pra cortar Nexo deliberadamente, user precisa
// falar com volume normal por ~0.4s (antes era ~0.2s, falha em
// ruído contínuo). Env vars (REACT_APP_VAD_RMS_BARGEIN /
// REACT_APP_VAD_BARGEIN_HOLD_MS) continuam disponíveis pra ajuste
// fino por usuário com mic ruim ou ambiente silencioso.
const DEFAULTS = {
  rmsStart: 0.018,
  rmsStartSpeaker: 0.030,        // when output device is a built-in speaker
  rmsEnd: 0.010,
  rmsBargeIn: 0.075,             // V8 — sobe de 0.050 (Bug A: falso positivo)
  startHoldMs: 80,
  // H2 hotfix kept this at 1500ms to absorb natural pauses ("e... daí...").
  endHoldMs: 1500,
  // Hysteresis: number of consecutive samples below rmsEnd required before we
  // even start the endHoldMs timer. Avoids false positives on brief inter-word
  // dips that drop below threshold for one frame.
  holdSamples: 3,
  bargeInHoldMs: 400,            // V8 — sobe de 200ms (Bug A: cough/breath/AC)
};

// VOICE-FIX-WARMUP — grace period após cada audio.play() onde a
// detecção de barge-in fica suprimida. O transiente inicial do TTS
// arrancando emite energia alta no built-in speaker do MacBook; o
// echo cancellation não cobre 100% e o mic capta resíduo. RMS chega
// acima do threshold V8 sustained por 400ms → falso positivo cortava
// o TTS no início. 1500ms cobre decoder warmup + speaker decay
// natural. Override via REACT_APP_VOICE_SPEAK_WARMUP_MS.
const _SPEAK_WARMUP_MS_DEFAULT = 1500;

const _envInt = (key, fallback, min, max) => {
  if (typeof process === "undefined" || !process.env) return fallback;
  const raw = process.env[key];
  if (raw === undefined || raw === null || raw === "") return fallback;
  const v = parseInt(raw, 10);
  if (!Number.isFinite(v) || v < min || v > max) return fallback;
  return v;
};

const _envFloat = (key, fallback, min, max) => {
  if (typeof process === "undefined" || !process.env) return fallback;
  const raw = process.env[key];
  if (raw === undefined || raw === null || raw === "") return fallback;
  const v = parseFloat(raw);
  if (!Number.isFinite(v) || v < min || v > max) return fallback;
  return v;
};

export default function usePreWarmedMic({
  onVoiceStart,
  onVoiceEnd,
  onBargeIn,
  rmsStart: rmsStartProp,
  rmsEnd: rmsEndProp,
  rmsBargeIn: rmsBargeInProp,
  startHoldMs: startHoldMsProp,
  endHoldMs: endHoldMsProp,
  holdSamples: holdSamplesProp,
  bargeInHoldMs: bargeInHoldMsProp,
} = {}) {
  // Tunables resolve env > prop > default.
  const rmsStart = _envFloat("REACT_APP_VAD_RMS_START", rmsStartProp ?? DEFAULTS.rmsStart, 0.001, 0.5);
  const rmsStartSpeaker = _envFloat("REACT_APP_VAD_RMS_START_SPEAKER", DEFAULTS.rmsStartSpeaker, 0.001, 0.5);
  const rmsEnd = _envFloat("REACT_APP_VAD_RMS_END", rmsEndProp ?? DEFAULTS.rmsEnd, 0.0005, 0.5);
  const rmsBargeIn = _envFloat("REACT_APP_VAD_RMS_BARGEIN", rmsBargeInProp ?? DEFAULTS.rmsBargeIn, 0.005, 0.8);
  const startHoldMs = _envInt("REACT_APP_VAD_START_HOLD_MS", startHoldMsProp ?? DEFAULTS.startHoldMs, 0, 2000);
  const endHoldMs = _envInt("REACT_APP_VAD_END_HOLD_MS", endHoldMsProp ?? DEFAULTS.endHoldMs, 100, 10000);
  const holdSamples = _envInt("REACT_APP_VAD_HOLD_SAMPLES", holdSamplesProp ?? DEFAULTS.holdSamples, 1, 50);
  const bargeInHoldMs = _envInt("REACT_APP_VAD_BARGEIN_HOLD_MS", bargeInHoldMsProp ?? DEFAULTS.bargeInHoldMs, 50, 2000);
  // VOICE-FIX-WARMUP — grace pós audio.play() (ms). 0 = sem warmup.
  const speakWarmupMs = _envInt(
    "REACT_APP_VOICE_SPEAK_WARMUP_MS",
    _SPEAK_WARMUP_MS_DEFAULT, 0, 5000,
  );

  const [state, setState] = useState("cold");
  const [isSpeaking, setIsSpeaking] = useState(false);
  // Whether the active output device looks like a built-in speaker (no
  // headset). When true we raise the start threshold to ignore residual
  // echo bleeding through software AEC. Best-effort — APIs vary across
  // browsers and the user can rotate devices mid-session.
  const [isOutputSpeaker, setIsOutputSpeaker] = useState(true);
  // VOICE-C1 F1 — Measured noise floor (RMS over 1s silence at mount) and
  // the thresholds derived from it. Falls back to V2 defaults until
  // calibration completes (~1s after prewarm()).
  const [noiseFloor, setNoiseFloor] = useState(0);
  const dynamicStartRef = useRef(null);
  const dynamicBargeInRef = useRef(null);

  // Refs — closures inside the RAF loop must read live values.
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const bufRef = useRef(null);
  const rafRef = useRef(null);
  const cbStartRef = useRef(onVoiceStart);
  const cbEndRef = useRef(onVoiceEnd);
  const cbBargeRef = useRef(onBargeIn);
  const speakingRef = useRef(false);
  const aboveSinceRef = useRef(0);
  const belowSinceRef = useRef(0);
  // Hysteresis counter: consecutive samples below rmsEnd while we are in
  // the speaking branch.
  const belowSamplesRef = useRef(0);
  // Mode: "normal" | "muted" | "bargein". Set by the orchestrator.
  const modeRef = useRef("normal");
  const isOutputSpeakerRef = useRef(true);
  // Sustained-above counter for barge-in detection.
  const bargeAboveSinceRef = useRef(0);
  const bargeFiredRef = useRef(false);

  // VOICE-FIX-WARMUP — ts do último audio.play(). O loop em modo
  // "bargein" ignora detecção enquanto elapsed < speakWarmupMs.
  // Marcado por markSpeakSegmentStart() (chamado pelo useJarvisVoice
  // dentro de audio.onplay) e zerado em finish()/stopSpeaking().
  const speakSegmentStartedAtRef = useRef(0);

  useEffect(() => { cbStartRef.current = onVoiceStart; }, [onVoiceStart]);
  useEffect(() => { cbEndRef.current = onVoiceEnd; }, [onVoiceEnd]);
  useEffect(() => { cbBargeRef.current = onBargeIn; }, [onBargeIn]);
  useEffect(() => { isOutputSpeakerRef.current = isOutputSpeaker; }, [isOutputSpeaker]);

  // Detect whether the default output device looks like a built-in speaker.
  // Best-effort across browsers — device labels are only populated after
  // permission is granted, so we re-run after prewarm().
  //
  // VOICE-C1 F4 — delegate the classification to pickOutputDeviceClass which
  // adds cascade fallbacks for iOS Safari and generic mobile, where labels
  // are typically empty even after permission.
  const detectOutputDevice = useCallback(async () => {
    try {
      let outputs = [];
      if (navigator?.mediaDevices?.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        outputs = devices.filter((d) => d.kind === "audiooutput");
      }
      const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
      const cls = pickOutputDeviceClass({ outputs, userAgent: ua });
      setIsOutputSpeaker(cls === "speaker");
    } catch (_) {
      setIsOutputSpeaker(true);
    }
  }, []);

  // VOICE-C1 F1 — Capture 1s of analyser samples and compute average RMS as
  // the user's noise floor. Adds ~1s latency to prewarm() but the result
  // tunes thresholds to the actual environment (quiet office vs noisy
  // café). Best-effort: if analyser is gone (teardown raced), bail to
  // V2 defaults.
  const calibrateNoiseFloor = useCallback(async (analyser, buf) => {
    if (!analyser || !buf) return 0;
    const SAMPLES = 20;          // ~20 frames at requestAnimationFrame (~16ms)
    const FRAME_DELAY_MS = 50;   // ~1s total
    let totalRms = 0;
    let collected = 0;
    for (let i = 0; i < SAMPLES; i++) {
      try {
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let j = 0; j < buf.length; j++) sum += buf[j] * buf[j];
        totalRms += Math.sqrt(sum / buf.length);
        collected += 1;
      } catch (_) { break; }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, FRAME_DELAY_MS));
    }
    if (collected === 0) return 0;
    const floor = totalRms / collected;
    const dyn = computeDynamicThresholds(floor);
    dynamicStartRef.current = dyn.rmsStart;
    dynamicBargeInRef.current = dyn.rmsBargeIn;
    setNoiseFloor(floor);
    console.log("[prewarm] noise_floor_calibrated", {
      floor: floor.toFixed(4),
      rmsStart_dyn: dyn.rmsStart.toFixed(4),
      rmsBargeIn_dyn: dyn.rmsBargeIn.toFixed(4),
    });
    return floor;
  }, []);

  const teardown = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (analyserRef.current) { try { analyserRef.current.disconnect(); } catch (_) {} analyserRef.current = null; }
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (_) {} audioCtxRef.current = null; }
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch (_) {}
      streamRef.current = null;
    }
    speakingRef.current = false;
    setIsSpeaking(false);
    setState("cold");
  }, []);

  const loop = useCallback(() => {
    const analyser = analyserRef.current;
    const buf = bufRef.current;
    if (!analyser || !buf) return;

    analyser.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    const rms = Math.sqrt(sum / buf.length);
    const now = performance.now();
    const mode = modeRef.current;

    if (mode === "muted") {
      // Track is hardware-muted; analyser will read silence. Reset everything
      // so we do not accumulate stale state.
      speakingRef.current = false;
      aboveSinceRef.current = 0;
      belowSinceRef.current = 0;
      belowSamplesRef.current = 0;
      bargeAboveSinceRef.current = 0;
      bargeFiredRef.current = false;
      rafRef.current = requestAnimationFrame(loop);
      return;
    }

    if (mode === "bargein") {
      // VOICE-FIX-WARMUP — grace period no arranque de cada TTS.
      // Eco do decoder do speaker no built-in mic do MacBook gera
      // RMS > threshold nos primeiros ~500-800ms mesmo com track
      // hardware-muted (echo cancellation imperfeito). Sustained
      // pode passar do bargeInHoldMs e disparar falso positivo.
      // Reset do sustained-counter pra evitar carry-over.
      const warmupStartedAt = speakSegmentStartedAtRef.current;
      if (warmupStartedAt > 0 && speakWarmupMs > 0) {
        const elapsed = now - warmupStartedAt;
        if (elapsed < speakWarmupMs) {
          bargeAboveSinceRef.current = 0;
          rafRef.current = requestAnimationFrame(loop);
          return;
        }
      }
      // While TTS speaks we ignore voice-start/end and only watch for a
      // sustained, loud-enough cut-in. One shot per speaking segment;
      // setBargeInMode("normal" | "muted") resets bargeFiredRef.
      //
      // VOICE-C1 F1+F5 — use dynamic threshold derived from noise floor
      // and adjusted by telemetry false-positive history when available.
      const baseBargeIn = dynamicBargeInRef.current ?? rmsBargeIn;
      const bargeInThreshold = getAdjustedBargeInThreshold(baseBargeIn);
      if (!bargeFiredRef.current && rms > bargeInThreshold) {
        if (!bargeAboveSinceRef.current) bargeAboveSinceRef.current = now;
        if (now - bargeAboveSinceRef.current >= bargeInHoldMs) {
          bargeFiredRef.current = true;
          cbBargeRef.current?.({ thresholdUsed: bargeInThreshold });
        }
      } else if (rms <= bargeInThreshold) {
        bargeAboveSinceRef.current = 0;
      }
      rafRef.current = requestAnimationFrame(loop);
      return;
    }

    // Normal mode — voice activity detection for the user's utterance.
    // VOICE-C1 F1 — prefer dynamic threshold once calibration finished.
    // Speaker mode keeps the elevated floor when on speakers (echo bleed).
    const dynStart = dynamicStartRef.current;
    const v2Start = isOutputSpeakerRef.current ? rmsStartSpeaker : rmsStart;
    const startThreshold = dynStart !== null ? Math.max(dynStart, v2Start) : v2Start;
    if (!speakingRef.current) {
      if (rms > startThreshold) {
        if (!aboveSinceRef.current) aboveSinceRef.current = now;
        if (now - aboveSinceRef.current >= startHoldMs) {
          speakingRef.current = true;
          setIsSpeaking(true);
          belowSinceRef.current = 0;
          belowSamplesRef.current = 0;
          cbStartRef.current?.();
        }
      } else {
        aboveSinceRef.current = 0;
      }
    } else {
      if (rms < rmsEnd) {
        belowSamplesRef.current += 1;
        // Hysteresis — only START measuring endHoldMs after holdSamples
        // consecutive samples below rmsEnd. A single dip resets the counter.
        if (belowSamplesRef.current >= holdSamples) {
          if (!belowSinceRef.current) belowSinceRef.current = now;
          if (now - belowSinceRef.current >= endHoldMs) {
            speakingRef.current = false;
            setIsSpeaking(false);
            aboveSinceRef.current = 0;
            belowSinceRef.current = 0;
            belowSamplesRef.current = 0;
            cbEndRef.current?.();
          }
        }
      } else {
        belowSinceRef.current = 0;
        belowSamplesRef.current = 0;
      }
    }

    rafRef.current = requestAnimationFrame(loop);
  }, [rmsStart, rmsStartSpeaker, rmsEnd, rmsBargeIn, startHoldMs, endHoldMs, holdSamples, bargeInHoldMs]);

  const prewarm = useCallback(async () => {
    if (streamRef.current) return true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          // Whisper-friendly sample rate; harmless to Web Speech API. Leaves
          // room for a future migration to server-side STT without changing
          // the capture stack.
          sampleRate: 16000,
        },
      });
      streamRef.current = stream;

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;

      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.2;
      src.connect(analyser);
      analyserRef.current = analyser;
      bufRef.current = new Float32Array(analyser.fftSize);

      if (ctx.state === "suspended") { try { await ctx.resume(); } catch (_) {} }

      setState("prewarmed");
      // Run output-device detection now that labels are populated post-permission.
      detectOutputDevice();
      // VOICE-C1 F1 — fire-and-forget: 1s ambient sample → dynamic thresholds.
      // Loop starts immediately on V2 defaults; thresholds swap mid-flight
      // once calibration resolves (refs are read each frame).
      calibrateNoiseFloor(analyser, bufRef.current).catch(() => {});
      rafRef.current = requestAnimationFrame(loop);
      return true;
    } catch (e) {
      console.warn("[prewarm] getUserMedia failed", e);
      setState("error");
      return false;
    }
  }, [loop, detectOutputDevice]);

  const ensurePrewarmed = useCallback(() => {
    if (streamRef.current) return Promise.resolve(true);
    return prewarm();
  }, [prewarm]);

  /**
   * Hardware-mute or unmute the captured audio track. Used while TTS is
   * speaking to defeat speaker → mic acoustic loops that getUserMedia's
   * echoCancellation cannot fully suppress.
   */
  const setMicEnabled = useCallback((enabled) => {
    const stream = streamRef.current;
    if (!stream) return false;
    try {
      stream.getAudioTracks().forEach((t) => { t.enabled = !!enabled; });
      return true;
    } catch (_) { return false; }
  }, []);

  /**
   * Switch VAD mode:
   *   "normal" — onVoiceStart/onVoiceEnd active (default)
   *   "muted"  — analyser ignored, no callbacks (use during PROCESSING gap)
   *   "bargein"— only onBargeIn fires, sustained above rmsBargeIn
   *
   * Switching out of any mode resets the barge-in latch so the next TTS
   * segment can fire it again.
   */
  const setBargeInMode = useCallback((mode) => {
    const next = mode === "muted" || mode === "bargein" ? mode : "normal";
    modeRef.current = next;
    bargeFiredRef.current = false;
    bargeAboveSinceRef.current = 0;
    if (next !== "normal") {
      // Drop visible "speaking" indicator — nothing should pulse the orb
      // during TTS.
      speakingRef.current = false;
      setIsSpeaking(false);
      aboveSinceRef.current = 0;
      belowSinceRef.current = 0;
      belowSamplesRef.current = 0;
    }
    return next;
  }, []);

  // React to runtime device changes (plug/unplug headphones).
  useEffect(() => {
    if (!navigator?.mediaDevices?.addEventListener) return undefined;
    const handler = () => detectOutputDevice();
    try {
      navigator.mediaDevices.addEventListener("devicechange", handler);
    } catch (_) { return undefined; }
    return () => {
      try { navigator.mediaDevices.removeEventListener("devicechange", handler); } catch (_) {}
    };
  }, [detectOutputDevice]);

  useEffect(() => () => teardown(), [teardown]);

  // VOICE-DEBUG-PANEL — exposto pro VoiceDebugPanel ler track.enabled
  // e track.label sem mexer no streamRef. Retorna null se prewarm nunca
  // rodou; callers usam optional-chain.
  const getMicTrack = useCallback(() => {
    try {
      return streamRef.current?.getAudioTracks?.()?.[0] || null;
    } catch (_) {
      return null;
    }
  }, []);

  // VOICE-FIX-WARMUP — chamado por useJarvisVoice em audio.onplay
  // (cada novo segmento de TTS reseta o warmup). Loop em "bargein"
  // ignora detecção enquanto elapsed < speakWarmupMs.
  const markSpeakSegmentStart = useCallback(() => {
    speakSegmentStartedAtRef.current = performance.now();
  }, []);

  const clearSpeakSegmentStart = useCallback(() => {
    speakSegmentStartedAtRef.current = 0;
  }, []);

  const getSpeakWarmupRemaining = useCallback(() => {
    const ts = speakSegmentStartedAtRef.current;
    if (!ts) return 0;
    const elapsed = performance.now() - ts;
    return Math.max(0, speakWarmupMs - elapsed);
  }, [speakWarmupMs]);

  return {
    state, isSpeaking, isOutputSpeaker,
    prewarm, ensurePrewarmed, teardown,
    setMicEnabled, setBargeInMode,
    getMicTrack,
    markSpeakSegmentStart, clearSpeakSegmentStart, getSpeakWarmupRemaining,
  };
}
